/*
 * COLLECTIVE INTELLIGENCE GOVERNANCE (F5) — OWNER ONLY, read-only, synthetic-only.
 * Three distinct states are never blurred: not active here, could not be read, and not yet
 * built (G3). The restricted contribution ledger is never read.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Network } from "lucide-react";
import { Panel, Note, Chip, fmt, FreshnessNote } from "./ConsolePrimitives";
import { T2FindingsReview } from "./T2FindingsReview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/founder/ci-governance`;

const Counts = ({ rows, a, b, prefix }) => (
  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
    {(rows || []).map((r) => (
      <Chip key={`${r[a]}-${r[b]}`} tone="quiet" testId={`${prefix}-${r[a]}-${r[b]}`}>
        {r[a]} / {r[b]}: {r.suppressed ? "withheld" : r.count}
      </Chip>
    ))}
  </div>
);

export const CIGovernanceView = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(API);
      setData(res.data);
    } catch (e) {
      setError("Collective Intelligence governance could not be read just now.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const chain = data?.audit_chain;

  return (
    <>
      <header className="fc__section-head">
        <h2 data-testid="fc-ci-title">Collective Intelligence</h2>
        <p data-testid="fc-ci-banner">{data?.banner}</p>
      </header>
      <Panel title="Governance state" icon={Network} testId="fc-ci-panel">
        {data && !data.available && <Note testId="fc-ci-unavailable">{data.reason}</Note>}
        {data?.environment && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Chip tone={data.environment.master_enabled ? "warn" : "quiet"} testId="fc-ci-master">master switch: {data.environment.master_enabled ? "on" : "off"}</Chip>
            <Chip tone={data.environment.kill_switch_env || data.control?.killed ? "warn" : "quiet"} testId="fc-ci-kill">
              kill switch: {data.environment.kill_switch_env || data.control?.killed ? "engaged" : "not engaged"}
            </Chip>
          </div>
        )}
        {data?.available && (
          <>
            <p style={{ marginTop: 12 }}>Candidates: <b data-testid="fc-ci-candidates">{fmt(data.candidates?.value)}</b> · Runs: <b data-testid="fc-ci-runs">{fmt(data.runs?.value)}</b></p>
            <Counts rows={data.candidates_by_state} a="state" b="class" prefix="fc-ci-cand" />
            <Counts rows={data.runs_by_state} a="state" b="data_class" prefix="fc-ci-run" />
            <p style={{ marginTop: 12 }} data-testid="fc-ci-version">
              Active knowledge version: {data.knowledge_version?.active_kv_id || "none"}
            </p>
            <p data-testid="fc-ci-chain">
              Audit chain: {chain?.linkage_ok ? "linked" : "linkage broken"} across {chain?.entries_checked} recent entries · head {chain?.head_seq ?? "—"} · anchor {chain?.anchor_state}
            </p>
            <Note>{chain?.method}</Note>
          </>
        )}
        {data?.not_yet_built && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            {Object.entries(data.not_yet_built).map(([k, v]) => (
              <Chip key={k} tone="quiet" testId={`fc-ci-notbuilt-${k}`}>{k.replace("_", " ")}: {v}</Chip>
            ))}
          </div>
        )}
        {error && <Note testId="fc-ci-error">{error}</Note>}
      </Panel>
      <T2FindingsReview />
      {data?.freshness && <FreshnessNote freshness={data.freshness} />}
    </>
  );
};
